import type { ModalId } from "@@/modals/context/modal-id";
import { Modal } from "@@/modals/modal";
import { ModalHeader } from "@@/modals/modal-header";
import { useModalActions } from "@@/modals/use-modal-actions";
import { browserLogger } from "@@/settings/browser-logger";
import { EmptyState } from "@@/shared/components/empty-state";
import { Row } from "@@/shared/components/row";
import { VirtualizedList } from "@@/shared/components/virtualized-list";
import { Conditional } from "@@/shared/conditional";
import { HorizontalDivider, VerticalDivider } from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { Inputs } from "@@/shared/form/standalones/form2/inputs";
import { CheckBoxWithText, TextInput } from "@@/shared/text/text-input";
import { useTranslate } from "@@/translations/use-translate";
import { useTheme } from "@emotion/react";
import type {
    Product,
    ProductId,
    Translatable,
    TranslatableValueFactoryParams,
} from "@towni/common";
import { remSize, translation } from "@towni/common";
import * as React from "react";
import { ProductPickerRow } from "./product-picker-row";
import { useAvailableProducts } from "./use-avaible-bookable-products";

type Props = {
    modalId: ModalId;
    title?: Translatable | TranslatableValueFactoryParams;
    products: Product[];
    onToggle: (productId: ProductId) => void;
    selected: Set<ProductId>;
    mode: "SINGLE" | "MULTI";
};

const ProductPickerModal = (props: Props) => {
    const theme = useTheme();
    const [_textFilter, setTextFilter] = React.useState("");
    const { title, modalId } = props;
    const { hide } = useModalActions(props.modalId);

    const onToggle = React.useCallback(
        (productId: ProductId) => {
            props.onToggle(productId);
            if (props.mode === "SINGLE") hide();
        },
        [hide, props],
    );

    return (
        <Modal
            header={
                <ModalHeader
                    modalId={modalId}
                    bottomBorder
                    title={
                        title ??
                        (props.mode === "MULTI"
                            ? translation({
                                  sv: "Välj produkter",
                                  en: "Select products",
                              })
                            : translation({
                                  sv: "Välj produkt",
                                  en: "Select product",
                              }))
                    }
                    topRightButton={
                        props.mode === "MULTI"
                            ? {
                                  _type: "TOP_RIGHT_ACTION_TEXT",
                                  text: translation({
                                      sv: "Klar",
                                      en: "Ready",
                                  }),
                                  action: () => {
                                      hide();
                                      // setSelectedFilter(false);
                                      setTextFilter("");
                                  },
                              }
                            : undefined
                    }
                />
            }
            modalId={modalId}>
            <FlexColumn
                fillParentWidth
                crossAxis="flex-start"
                overflowY="hidden"
                background={{ color: theme.colors.default.background }}
                css={{
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10,
                }}>
                <ProductPickerComponent {...props} onToggle={onToggle} />
            </FlexColumn>
        </Modal>
    );
};
type PickerProps = {
    title?: Translatable | TranslatableValueFactoryParams;
    products: Product[];
    onToggle: (productId: ProductId) => void;
    selected: Set<ProductId>;
    mode: "SINGLE" | "MULTI";
};
const ProductPickerComponent = (props: PickerProps) => {
    const theme = useTheme();
    const translate = useTranslate();
    const [, startTransition] = React.useTransition();
    const [textFilter, setTextFilter] = React.useState("");
    const [activeFilter, setActiveFilter] = React.useState(true);
    const [selectedFilter, setSelectedFilter] = React.useState(false);
    const { products, selected } = props;
    const [availableProducts] = useAvailableProducts(
        products.length > 0 ? products[0].providerId : undefined,
    );
    const filteredProducts = React.useMemo(() => {
        return products.filter(product => {
            // Filter by text value
            const textFilterMatch =
                !textFilter ||
                translate(product.meta.title)
                    .toLocaleLowerCase()
                    .includes(textFilter.toLocaleLowerCase());

            // Filter if selected
            const selectedFilterMatch =
                !selectedFilter || selected.has(product._id);

            // Hide all inactive products; that are not already selected
            if (product.status.type !== "ACTIVE" && !selectedFilterMatch) {
                return false;
            }

            const hasSessionAndFiltered =
                !activeFilter ||
                product.acquirableAs !== "BOOKABLE" ||
                availableProducts?.some(p => p._id === product._id);

            // TODO! filter by provider names as well
            return (
                textFilterMatch && selectedFilterMatch && hasSessionAndFiltered
            );
        });
    }, [
        products,
        textFilter,
        translate,
        selectedFilter,
        selected,
        activeFilter,
        availableProducts,
    ]);

    const onToggleRef = React.useRef(props.onToggle);
    onToggleRef.current = props.onToggle;
    const renderRow = React.useCallback(
        (index: number, product: Product) => {
            const isFirst = index === 0;
            const isLast = index === filteredProducts.length - 1;

            return (
                <ProductPickerRow
                    key={product._id}
                    product={product}
                    toggle={onToggleRef.current}
                    selected={selected.has(product._id)}
                    padding={{
                        top: isFirst ? 20 : 12,
                        bottom: isLast ? 40 : 0,
                    }}
                />
            );
        },
        [filteredProducts.length, selected],
    );
    const EmptyPlaceholder = () => (
        <Row className="size-full p-5">
            <EmptyState
                className="flex-1"
                text={{
                    sv: "Inga produkter matchar filtreringen",
                    en: "No products match the filter",
                }}
            />
        </Row>
    );

    return (
        <>
            <FlexColumn
                fillParentWidth
                background={{
                    color: theme.isLightTheme
                        ? theme.colors.primary.light
                        : theme.colors.default.background.value.withLight(0.1),
                }}
                crossAxis="flex-start"
                padding={{ leftRight: 20, top: 0 }}>
                <VerticalDivider S />
                <FlexRow fillParentWidth mainAxis="space-between">
                    <TextInput
                        type="text"
                        grow={1}
                        padding={{ leftRight: 15, topBottom: 12 }}
                        size={remSize(0.875)}
                        placeholder={translation({
                            sv: "Sök",
                            en: "Search",
                        })}
                        initialValue={textFilter}
                        onChange={value => {
                            startTransition(() => setTextFilter(value));
                        }}
                        onValidationFailed={browserLogger.log}
                    />

                    <Conditional when={props.mode === "MULTI"}>
                        <HorizontalDivider S />
                        <CheckBoxWithText
                            checked={selectedFilter}
                            text={
                                props.mode === "MULTI"
                                    ? translation({
                                          sv: "Endast valda",
                                          en: "Only selected",
                                      })
                                    : translation({
                                          sv: "Endast vald",
                                          en: "Only selected",
                                      })
                            }
                            onChange={checked => {
                                startTransition(() =>
                                    setSelectedFilter(checked),
                                );
                            }}
                        />
                    </Conditional>
                </FlexRow>
                <VerticalDivider S />
                <Inputs.Checkbox
                    initialValue={activeFilter}
                    onChange={setActiveFilter}
                    text={translation({
                        sv: "Visa bara aktiva och bokningsbara",
                        en: "Show only active and bookable",
                    })}
                />
                <VerticalDivider S />
            </FlexColumn>
            <Row crossAxis="stretch" className="h-[60vh] w-full">
                <VirtualizedList
                    className="flex-1"
                    data={filteredProducts}
                    itemContent={renderRow}
                    components={{
                        EmptyPlaceholder,
                    }}
                />
            </Row>
        </>
    );
};

export { ProductPickerComponent, ProductPickerModal };
